import { useMemo, useState } from 'react';
import {
  HOME3_STATE_LABEL_KEY,
  agentCount,
  home3StateBadge,
  emptyFeedMessage,
  feedRevealControl,
  feedRowCap,
  filterFeed,
  home3AgentName,
  home3EmptyState,
  showsFeedShowAll,
  visibleFeedRows,
  visibleStates,
  type Home3ReadState,
  type Home3Event,
  type Home3StateFilter,
} from '../../../lib/home3-feed';
import { useTranslation } from '../../../lib/i18n';
import { ENTITY_TYPE, useGenericBi } from '../../../lib/use-generic-bi';
import { patchRoutine, useRoutines } from '../../../lib/routines';
import { useAgentName } from '../../../lib/use-agent-name';
import { Modal } from '../../Modal';
import { cardActionsFor, useOutreachCardActionRunner } from './OutreachCardModal';
import { useHome3EventOpener } from './use-home3-event-opener';
import { useOutreachCardContext } from './use-outreach-card-context';
import type { SentOutreachSummary } from '../../../lib/outreach-rows';
import { outreachSiblings } from '../../../lib/outreach-thread-stepper';
import type { ThreadSibling } from '../outreach/ThreadStepper';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { Badge, Button, Card, OverflowMenu, TextButton, Typography } from '../ui';
import { AgentDot } from '../../AgentDot';
import { Home3EmptyState } from './Home3EmptyState';
import { Home3FeedRow } from './Home3FeedRow';
import styles from './Home3Feed.module.css';
import { isHomeEmptyForced } from '../../../lib/dev-home-empty-states';

export const HOME3_PAGE = 'home_overview';

/**
 * The Overview home's "All" list — every kind of item, newest first (Figma `19106:277489`). A category
 * tile opens that category's own page instead (`Home3CategoryView`), so this list is never narrowed to
 * one destination.
 *
 * The status picker beside the title narrows the rows; "Show more" at the card's foot reveals
 * `HOME3_SHOW_MORE_STEP` more at a time.
 *
 * ## A row opens the REAL drill-in when it has one
 *
 * A row backed by a task pushes the shared `TaskDetailModal` onto `TaskSidePanelContext` — the same
 * drill-in the Tasks list, the island's quick view and the goal plan open. A goal, routine or file row
 * opens that item's own drill-in. Only a row with neither (an app an agent built) falls back to this
 * screen's own small modal.
 */
export function Home3Feed({
  events,
  narrow,
  viewportNarrow,
  agentNames,
  spaceNames,
  readState,
  regionId,
}: {
  /** Every feed row — the counts need them all even when the status picker narrows the rows. */
  events: Home3Event[];
  /** Measured on the screen's own container, never the viewport. See `Home3Body`. */
  narrow: boolean;
  /** The true VIEWPORT narrow signal (`useViewportNarrow`, 720) — a desktop with the chat docked keeps
   *  its desktop treatment even when the pane is squeezed under 700. The status picker's desktop-only
   *  badge size reads this, not `narrow`, so the docked chat can't drop it to the mobile size. */
  viewportNarrow: boolean;
  agentNames: Record<string, string>;
  /** Space id → name, for the rows' second line. Resolved by the screen, which reads the workspaces. */
  spaceNames: Record<string, string>;
  /** Whether the rows can be trusted yet. `ready` is the only state that may say "no activity". */
  readState: Home3ReadState;
  /** This region's DOM id — the cards' `aria-controls` target. */
  regionId: string;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const maestro = useAgentName();
  const [state, setState] = useState<Home3StateFilter>(null);
  // 0 or 1: one "Show more" tap expands the list a step, and "Show less" folds it back.
  const [showMoreTaps, setShowMoreTaps] = useState(0);
  const [showAllRows, setShowAllRows] = useState(false);
  // Who a Messages card is with and where its actions go (contact book + profile-link leads).
  const cardContext = useOutreachCardContext();
  const runCardAction = useOutreachCardActionRunner(HOME3_PAGE);
  const { state: routinesState } = useRoutines();
  // What a row opens — shared with the canvas's activity panel (`useHome3EventOpener`).
  const opener = useHome3EventOpener({ agentNames, spaceNames });

  /** Flips a routine row's switch — the same write, BI name and rollback the Routines screen uses. */
  const toggleRoutine = (event: Home3Event, next: boolean) => {
    const id = event.item?.kind === 'routine' ? event.item.id : undefined;
    const routine = id && routinesState.kind === 'ready' ? routinesState.routines.find((r) => r.id === id) : undefined;
    if (!routine) return;
    logPageClickBi(
      { page_name: HOME3_PAGE, button_name: 'toggle_routine', state: next ? 'on' : 'off' },
      { componentName: 'feed-row-routines', entity: { id: routine.id, type: ENTITY_TYPE.pulse } },
    );
    void patchRoutine(routine, { paused: !next });
  };

  const states = useMemo(() => visibleStates(events), [events]);
  const forcedEmpty = isHomeEmptyForced();
  const rows = useMemo(() => (forcedEmpty ? [] : filterFeed(events, state, null)), [events, state, forcedEmpty]);
  const cap = feedRowCap(narrow);
  const shownCount = visibleFeedRows(cap, showMoreTaps, rows.length);
  const reveal = feedRevealControl(shownCount, rows.length, cap);
  const agents = useMemo(() => agentCount(events), [events]);
  // The distinct acting agents, in the order they appear — the marks for the footnote. Same set
  // `agentCount` sizes, so the cluster and the number can never disagree.
  const agentIds = useMemo(() => {
    const seen: string[] = [];
    for (const event of events) {
      const id = event.subtitle.kind === 'agent' ? event.subtitle.agentId : '';
      if (id && !seen.includes(id)) seen.push(id);
    }
    return seen;
  }, [events]);
  const empty = emptyFeedMessage(state, null);
  // The designed empty state takes the card and drops the footnote; the heading and the Status picker stay
  // above it (Figma 21853:63500).
  const emptyState = home3EmptyState(readState, state, rows.length);

  // "47 events across 7 agents" — two counted clauses, each with its own plural form, joined by a key
  // the translator owns. An account with no agent-acted row shows the events half alone rather than
  // "across 0 agents", which is the same graceful-at-zero rule the empty line follows.
  const eventsLabel = t(events.length === 1 ? 'home.overview.feed.eventsOne' : 'home.overview.feed.eventsOther', { count: events.length });
  const agentsLabel = t(agents === 1 ? 'home.overview.feed.agentsOne' : 'home.overview.feed.agentsOther', { count: agents });
  const meta =
    readState !== 'ready' ? '' : agents > 0 ? t('home.overview.feed.meta', { events: eventsLabel, agents: agentsLabel }) : eventsLabel;

  const title = t('home.overview.recentActivity');

  const pickState = (next: Home3StateFilter) => {
    setState(next);
    setShowMoreTaps(0);
    setShowAllRows(false);
    logPageClickBi({ page_name: HOME3_PAGE, button_name: `filter_state_${next ?? 'all'}` });
  };

  // One renderer for the list and the "Show all" modal, so a row opens the same drill-in from either.
  // `beforeOpen` closes the modal first: a drill-in opened under it would sit behind its scrim.
  const cardActionsOf = (event: Home3Event) => {
    if (event.outreach?.kind !== 'sent') return undefined;
    const summary = event.outreach.thread as SentOutreachSummary;
    const actions = cardActionsFor(summary, cardContext(summary.id));
    return [
      ...(actions.reply ? [{ key: 'reply', label: t('outreachCard.action.reply'), onClick: () => runCardAction(actions, 'reply', summary.id) }] : []),
      ...(actions.openChat
        ? [{ key: 'openChat', label: t('outreachCard.action.openChat'), onClick: () => runCardAction(actions, 'openChat', summary.id) }]
        : []),
    ];
  };

  // The thread dialogs' Previous / Next steps through EVERY thread row of the list the row sits in — received
  // and sent mixed, in on-screen order (the card's shown slice, or every row in "Show all"), as on both
  // phones. Each sibling opens the dialog a tap on its row opens, handed the same list.
  const threadSibling = opener.threadSibling;
  const shownRows = rows.slice(0, shownCount);
  const cardSiblings = outreachSiblings(shownRows, threadSibling);
  const allSiblings = outreachSiblings(rows, threadSibling);

  const renderRow = (event: Home3Event, beforeOpen?: () => void, siblings: readonly ThreadSibling[] = cardSiblings) => (
    <Home3FeedRow
      actions={cardActionsOf(event)}
      key={event.id}
      event={event}
      agentName={home3AgentName(event, agentNames)}
      spaceName={(event.workspaceId && spaceNames[event.workspaceId]) || ''}
      showCategory
      maestroName={maestro.short}
      {...(event.toggle ? { onToggle: (next: boolean) => toggleRoutine(event, next) } : {})}
      onOpen={() => {
        beforeOpen?.();
        logPageClickBi(
          { page_name: HOME3_PAGE, button_name: 'open_event', ...(event.taskId ? { task_id: event.taskId } : {}) },
          {
            componentName: `feed-row-${event.destination}`,
            ...(event.taskId ? { entity: { id: event.taskId, type: ENTITY_TYPE.task } } : {}),
          },
        );
        opener.open(event, siblings);
      }}
    />
  );

  return (
    <section id={regionId} className={styles.feed} aria-label={title}>
      <div className={styles.headRow}>
        {/* Display 05 — Archivo SemiBold 16 (Design QA, Caro). The DS role, which the legacy `.sym-*`
          scale has no alias for: its 16 is the UI face and this line is the display one. */}
        <Typography variant="display-05" component="h2">
          {title}
        </Typography>
        {/* Tier 1, as a picker rather than a chip strip: a row of chips can only state the current
          value by being scanned.
          
          The trigger reads **"Status"** while nothing is filtered — which is what the frame draws
          (Figma 19697:59395) — and the VALUE once something is, so the control still answers "what
          am I looking at" without a second glance. Naming it "Status" in both states would have
          hidden an active filter behind a static word. Outlined, because the frame gives it an
          edge; it was the ghost type. `onClear` appears only away from the unfiltered state. */}
        <OverflowMenu
          ariaLabel={t('home.overview.filterState')}
          label={state ? t(HOME3_STATE_LABEL_KEY[state]) : t('home.overview.statusLabel')}
          triggerSize="small"
          /* DESKTOP: the open menu is the Figma Card Box (20018-107017) — a sunken card at padding 20/10,
           radius 20, its rows 16 apart, each row taking a grey ghost fill on hover — not the default
           compact Action Menu. Mobile web keeps the standard popover. */
          menuClassName={viewportNarrow ? undefined : styles.statusMenu}
          /* The frame is a Symphony Button at Skin=Standard, **Type=Ghost**, 86x30 (Design QA, Caro —
           Figma 20018:106749), which is `variant="text"` on our own primitive at `size="small"`.
           It was `outlined`: a boxed control beside a plain heading read as the loudest thing on the
           row, where the design wants the filter to recede until it is carrying a value. */
          triggerVariant="text"
          /* The picker sits at the row's trailing edge, so the menu RIGHT-aligns to the trigger (its
           default for a labelled picker is `start`, which left-aligned the menu and ran it off the
           pane's right edge — the misalignment). `end` opens it leftward, flush under "Status". */
          align="end"
          selectedKey={state ?? 'all'}
          /* No ✕ clear affordance: the Figma selected trigger (20102-59105) is just the value + chevron,
           and "All" in the open menu is the way back to unfiltered. */
          actions={[
            // "All" is not a status, so it stays plain text — badging it would invent a fourth state. No
            // count (the Figma shows bare labels). Wrapped so its hover (ink + underline, Figma 20114-63662)
            // can be styled; it is the one option that reads as a text button rather than a badge.
            { key: 'all', label: <span className={styles.allOption}>{t('home.overview.filter.all')}</span>, onSelect: () => pickState(null) },
            // ...and each real state wears its OWN badge, the same one its rows wear (Figma: the open
            // picker is a column of coloured badges). `home3Badge` is what maps a state to the DS
            // variant, so the colour here cannot drift from the colour in the list below.
            //
            // The picker badges are the DS **Tiny** size (20px, 12/16 SemiBold, -2%) on both viewports —
            // Figma 20018-107017. The hover is NOT on the badge: this frame highlights the whole ROW with
            // a grey ghost fill (see `.statusMenu` row :hover), so the badge stays inert and carries no
            // `hoverable` treatment.
            ...states.map((value) => ({
              key: value,
              label: (
                <Badge
                  variant={home3StateBadge(value).variant}
                  size="tiny"
                  label={t(HOME3_STATE_LABEL_KEY[value])}
                />
              ),
              onSelect: () => pickState(value),
            })),
          ]}
        />
      </div>

      {/* The DS SUNKEN card — white on the stone page — at the design's L size (Figma 19106:277381:
          "Skin=Sunken, size=L", radius 20, padding 20). It was `outline`/`sm`: a hairline with no fill,
          which on the light page left the list looking like bare page rather than a surface. `sunken`
          is the DS's own answer for "white card on a stone surface", so it flips correctly in dark
          mode too instead of pinning a white. */}
      <Card size="lg">
        {emptyState ? (
          <Home3EmptyState empty={emptyState} />
        ) : readState !== 'ready' ? (
          // NOT the empty line: a read that is still running, or that failed, has not established that
          // nothing happened. Saying "no activity" here would be the screen inventing an answer.
          <Typography variant="p2" color="secondary" className={styles.empty}>
            {t(readState === 'error' ? 'home.overview.readFailed' : 'home.overview.loading')}
          </Typography>
        ) : rows.length === 0 ? (
          // Never a blank card: the line says which filter produced the emptiness, naming only the
          // halves that are actually set.
          <Typography variant="p2" color="secondary" className={styles.empty}>
            {t(empty.key, { filters: empty.filterKeys.map((key) => t(key)).join(' ') })}
          </Typography>
        ) : (
          <div className={styles.rows}>
            {shownRows.map((event) => renderRow(event))}
          </div>
        )}

        {readState === 'ready' && reveal !== 'none' && (
          <div className={styles.seeAllRow}>
            {/* A ghost Button (not the DS TextButton) so it carries the SAME grey hover fill as the status
                picker beside the title — the chevron-down comes from the Figma (21639-655897). One tap
                turns it into "Show less" with a chevron-up (Figma 21882-133464), shared 1:1 with iOS
                `ActivityMapListHome.revealButton` / Android `RevealRow` through `feedRevealControl`. */}
            <Button
              variant="text"
              size="small"
              endIcon={reveal === 'showMore' ? <ChevronDown size={16} aria-hidden /> : <ChevronUp size={16} aria-hidden />}
              onClick={() => {
                setShowMoreTaps(reveal === 'showMore' ? 1 : 0);
                logPageClickBi({ page_name: HOME3_PAGE, button_name: reveal === 'showMore' ? 'show_more' : 'show_less' });
              }}
            >
              {t(reveal === 'showMore' ? 'home.overview.showMore' : 'home.overview.showLess')}
            </Button>
          </div>
        )}
      </Card>

      {/* The divider sits ABOVE the footnote row (Figma 21882-134420), separating the list from its
          "N events across M agents · Show all ›" summary. Narrow pane only: the desktop hides it in CSS. Web-only by construction: the phones
          draw only the narrow frame, which keeps it, so nothing is owed to native-apps. */}
      {meta && !emptyState && <hr className={styles.footDivider} />}

      {/* "47 events across 7 agents" — the whole list's footnote, under the card. */}
      {meta && !emptyState && (
        <p className={styles.meta}>
          {/* The agents behind the window, as their real marks — the design leads this line with the
              overlapping dot cluster (Figma 19106:277489's foot). Their own no-gap box, because the
              line's `gap` would cancel the negative margin and leave them flush; same construction,
              and the same -6 on a 20px dot, as the brief card's cluster and the island's. */}
          {agentIds.length > 0 && (
            <span className={styles.metaAvatars}>
              {agentIds.map((id) => (
                <span key={id} className={styles.metaAvatar}>
                  <AgentDot id={id} size={20} ringColor="var(--ds-surface-standard-bg)" />
                </span>
              ))}
            </span>
          )}
          <Typography variant="p3" color="secondary" component="span" className={styles.metaText}>
            {meta}
          </Typography>
          {showsFeedShowAll(shownCount, rows.length) && (
            <TextButton
              size="small"
              endIcon={<ChevronRight size={16} aria-hidden className="dir-flip" />}
              onClick={() => {
                logPageClickBi({ page_name: HOME3_PAGE, button_name: 'show_all_activity' });
                setShowAllRows(true);
              }}
            >
              {t('home.overview.showAll')}
            </TextButton>
          )}
        </p>
      )}

      <Modal
        open={showAllRows}
        onClose={() => setShowAllRows(false)}
        title={title}
        size="small"
        maxHeightPx={650}
        scrollBody
        presentation={narrow ? 'sheet' : 'centered'}
        footer={
          <Button variant="outlined" onClick={() => setShowAllRows(false)}>
            {t('common.close')}
          </Button>
        }
      >
        <div className={styles.sheetRows}>
          <div className={styles.rows}>{rows.map((event) => renderRow(event, () => setShowAllRows(false), allSiblings))}</div>
        </div>
      </Modal>

      {opener.modals}
    </section>
  );
}
